import React, { useState } from 'react';
import { ShieldIcon, LockIcon, CheckCircleIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Last Updated: January 2025

1. INFORMATION WE COLLECT
We collect personal information you provide when purchasing The 72-Hour Family Emergency Binder, including your name, email address, and payment information. We do not store credit card details directly — all payments are processed through secure third-party payment processors.

2. HOW WE USE YOUR INFORMATION
Your information is used solely to process your order, deliver your digital product, and communicate important updates about your purchase. We do not sell, rent, or share your personal information with third parties for marketing purposes.

3. DATA SECURITY
We implement industry-standard security measures, including 256-bit SSL encryption, to protect your personal information during transmission and storage.

4. COOKIES
Our website uses essential cookies to ensure proper functionality and to process transactions. We do not use tracking cookies for advertising purposes.

5. YOUR RIGHTS
You have the right to access, correct, or delete your personal information at any time. You may also opt out of any non-essential communications.

6. DATA RETENTION
We retain your purchase information for the duration required by applicable tax and accounting regulations. Digital delivery records are maintained to provide ongoing access to your product.

7. CHANGES TO THIS POLICY
We reserve the right to update this Privacy Policy at any time. Changes will be posted on this page with an updated revision date.
`.trim();
const termsContent = `
Last Updated: January 2025

1. ACCEPTANCE OF TERMS
By purchasing and/or using The 72-Hour Family Emergency Binder, you agree to be bound by these Terms and Conditions. If you do not agree, please do not complete your purchase.

2. PRODUCT DESCRIPTION
The 72-Hour Family Emergency Binder is a digital information product delivered electronically. It includes printable templates, checklists, and guides designed to help families organize critical documents for emergency preparedness.

3. DIGITAL DELIVERY
Upon successful payment, you will receive immediate access to download the digital product. Delivery is made via email and/or a secure download page.

4. PRICING AND PAYMENT
The current price is $6.39 USD. All prices are subject to change without notice. Payment is processed securely through our third-party payment provider.

5. REFUND POLICY
We offer a 30-day money-back guarantee. If you are not satisfied with your purchase, contact us within 30 days for a full refund. No questions asked. Bonus materials may be retained even after a refund.

6. INTELLECTUAL PROPERTY
All content within The 72-Hour Family Emergency Binder is protected by copyright. You are granted a personal, non-transferable license to use the materials for your family's preparedness planning. Redistribution, resale, or commercial use is strictly prohibited.

7. DISCLAIMER
The information provided is for educational and preparedness purposes only. It does not constitute professional medical, legal, or financial advice. We are not liable for any actions taken based on the information provided.

8. LIMITATION OF LIABILITY
To the maximum extent permitted by law, our total liability shall not exceed the purchase price of the product.

9. GOVERNING LAW
These terms shall be governed by and construed in accordance with the laws of the United States.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const openModal = (type: ModalType) => (e: React.MouseEvent) => {
    e.preventDefault();
    setActiveModal(type);
  };
  const closeModal = () => setActiveModal(null);
  const modalTitle = activeModal === 'privacy' ? 'Privacy Policy' : 'Terms & Conditions';
  const modalContent = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-dark-navy text-gray-400 py-12 px-4 border-t border-gray-800" data-id="element-32">
        <div className="max-w-7xl mx-auto" data-id="element-33">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8 pb-8 border-b border-gray-800" data-id="element-34">
            <div className="mb-6 md:mb-0" data-id="element-35">
              <h3 className="font-heading font-bold text-white text-xl uppercase tracking-widest mb-2" data-id="element-36">
                Family Preparedness
              </h3>
              <p className="font-heading text-xs uppercase tracking-widest" data-id="element-37">
                Official Documentation Center
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-6" data-id="element-38">
              <div className="flex items-center space-x-2 bg-navy px-3 py-2 rounded border border-gray-700" data-id="element-39">
                <LockIcon className="w-4 h-4 text-gray-300" data-id="element-40" />
                <span className="font-heading text-xs font-bold text-white uppercase tracking-wider" data-id="element-41">
                  Secure Checkout
                </span>
              </div>
              <div className="flex items-center space-x-2 bg-navy px-3 py-2 rounded border border-gray-700" data-id="element-42">
                <ShieldIcon className="w-4 h-4 text-gray-300" data-id="element-43" />
                <span className="font-heading text-xs font-bold text-white uppercase tracking-wider" data-id="element-44">
                  256-Bit SSL
                </span>
              </div>
              <div className="flex items-center space-x-2 bg-navy px-3 py-2 rounded border border-gray-700" data-id="element-45">
                <CheckCircleIcon className="w-4 h-4 text-gray-300" data-id="element-46" />
                <span className="font-heading text-xs font-bold text-white uppercase tracking-wider" data-id="element-47">
                  Guaranteed
                </span>
              </div>
            </div>
          </div>

          <div className="text-center max-w-4xl mx-auto space-y-6" data-id="element-48">
            <p className="font-body text-xs leading-relaxed text-gray-500" data-id="element-49">
              Disclaimer: The information provided in The 72-Hour Emergency
              Binder is for educational and preparedness purposes only. It is
              not intended to replace professional medical, legal, or financial
              advice. Always consult with qualified professionals regarding your
              specific situation.
            </p>

            {/* Legal Links — open modals */}
            <div className="flex items-center justify-center space-x-6 pt-2 border-t border-gray-800" data-id="element-50">
              <button onClick={openModal('privacy')} className="font-heading text-xs text-gray-400 hover:text-white uppercase tracking-widest transition-colors cursor-pointer bg-transparent border-none" data-id="element-51">
                Privacy Policy
              </button>
              <span className="text-gray-700" data-id="element-52">|</span>
              <button onClick={openModal('terms')} className="font-heading text-xs text-gray-400 hover:text-white uppercase tracking-widest transition-colors cursor-pointer bg-transparent border-none" data-id="element-53">
                Terms &amp; Conditions
              </button>
            </div>

            <p className="font-heading text-xs uppercase tracking-widest text-gray-600" data-id="element-54">
              &copy; {new Date().getFullYear()} Family Preparedness Systems. All
              Rights Reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Modal Popup */}
      <AnimatePresence data-id="element-55">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={closeModal} data-id="element-56">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.25
        }} className="bg-white w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl relative" onClick={e => e.stopPropagation()} data-id="element-57">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b-2 border-navy bg-light-gray flex-shrink-0" data-id="element-58">
                <div data-id="element-59">
                  <p className="font-heading text-[10px] text-gray-500 uppercase tracking-widest mb-1" data-id="element-60">
                    Legal Document
                  </p>
                  <h2 className="font-heading font-bold text-2xl text-navy uppercase tracking-wide" data-id="element-61">
                    {modalTitle}
                  </h2>
                </div>
                <button onClick={closeModal} className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-navy hover:bg-gray-200 transition-colors rounded-sm" aria-label="Close modal" data-id="element-62">
                  <XIcon className="w-5 h-5" data-id="element-63" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1" data-id="element-64">
                <div className="font-body text-sm text-body-text leading-relaxed whitespace-pre-line" data-id="element-65">
                  {modalContent}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-200 bg-light-gray flex-shrink-0 text-center" data-id="element-66">
                <button onClick={closeModal} className="font-heading text-sm font-bold text-navy uppercase tracking-widest hover:text-safety-red transition-colors" data-id="element-67">
                  Close Document
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}